import React, { useEffect, useRef } from "react";
import {
  Icon,
  TogglePill,
  TogglePillGroup,
  type TogglePillGroupProps
} from "@theorgchartdev/storybook";
import { useTabularDisplayContext } from "@/providers/TabularDisplayProvider";
import { DISPLAY_MODES } from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";

export type DisplayToggleProps = {
  onDisplayChange?: (display: (typeof DISPLAY_MODES)[keyof typeof DISPLAY_MODES]) => void;
};

const baseTogglePillClass = "oc-display-toggle-pill";

const DisplayToggle: React.FC<DisplayToggleProps> = ({ onDisplayChange }) => {
  const { displayMode, changeDisplayMode, shouldShowDisplayToggle, focusRestoreRequestedRef } =
    useTabularDisplayContext();

  const chartPillRef = useRef<HTMLDivElement>(null);
  const tablePillRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focusRestoreRequestedRef.current) return;
    focusRestoreRequestedRef.current = false;
    const activePillRef = displayMode === DISPLAY_MODES.TABLE ? tablePillRef : chartPillRef;
    activePillRef.current?.focus();
  }, [focusRestoreRequestedRef, displayMode]);

  if (!shouldShowDisplayToggle) {
    return null;
  }

  const handleDisplayChange: TogglePillGroupProps["onChange"] = value => {
    const displayValue = Array.isArray(value) && value.length > 0 ? value[0] : value;
    if (displayValue === DISPLAY_MODES.CHART || displayValue === DISPLAY_MODES.TABLE) {
      changeDisplayMode(displayValue);

      if (onDisplayChange) {
        onDisplayChange(displayValue);
      }
    }
  };

  return (
    <TogglePillGroup value={displayMode} onChange={handleDisplayChange} exclusive enforceValueSet>
      <TogglePill
        ref={chartPillRef}
        value={DISPLAY_MODES.CHART}
        aria-label="switch to chart display"
        className={baseTogglePillClass}
        data-testid={"chart-view-toggle"}
      >
        <Icon name="sitemap" size="small" />
        {appstr("Chart")}
      </TogglePill>
      <TogglePill
        ref={tablePillRef}
        value={DISPLAY_MODES.TABLE}
        aria-label="switch to table display"
        className={baseTogglePillClass}
        data-testid={"table-view-toggle"}
      >
        <Icon name="table" size="small" />
        {appstr("Table")}
      </TogglePill>
    </TogglePillGroup>
  );
};

export default DisplayToggle;
